import { zodResolver } from "@hookform/resolvers/zod"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useFormErrors } from "~/common/use-form-errors"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TextField } from "~/fields/text-field"
import { Button } from "~/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { Form } from "~/ui/form"
import { EmailVerificationStep } from "./components/EmailVerificationStep"

const emailFormSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
})

type EmailFormValues = z.infer<typeof emailFormSchema>

const USER_EMAIL_CREATE_MUTATION = gql(/* GraphQL */ `
  mutation AddEmailDialogUserEmailCreate($input: UserEmailCreateInput!) {
    userEmailCreate(input: $input) {
      userEmail {
        id
        email
        status
      }
    }
  }
`)

const USER_EMAIL_VERIFY_MUTATION = gql(/* GraphQL */ `
  mutation AddEmailDialogUserEmailVerify($input: UserEmailVerifyInput!) {
    userEmailVerify(input: $input) {
      userEmail {
        id
        email
        status
      }
    }
  }
`)

interface AddEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export const AddEmailDialog: React.FC<AddEmailDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [userEmailId, setUserEmailId] = useState<string>("")
  const [submittedEmail, setSubmittedEmail] = useState<string>("")
  const [userEmailCreate, userEmailCreateResult] = useSafeMutation(USER_EMAIL_CREATE_MUTATION)
  const [userEmailVerify, userEmailVerifyResult] = useSafeMutation(USER_EMAIL_VERIFY_MUTATION)

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: "",
    },
  })

  useFormErrors(emailForm.setError, userEmailCreateResult)

  const handleClose = () => {
    setEmailSubmitted(false)
    setUserEmailId("")
    setSubmittedEmail("")
    emailForm.reset()
    onOpenChange(false)
  }

  const onEmailSubmit = async (values: EmailFormValues) => {
    const result = await userEmailCreate({
      variables: {
        input: {
          userEmailInput: {
            email: values.email,
          },
        },
      },
    })

    if (result.errors) {
      return
    }

    setUserEmailId(result.data?.userEmailCreate.userEmail.id || "")
    setSubmittedEmail(values.email)
    setEmailSubmitted(true)
  }

  const handleVerify = async (token: string) => {
    const result = await userEmailVerify({
      variables: {
        input: {
          userEmailId,
          token,
        },
      },
    })

    if (result.errors) {
      return
    }

    onSuccess?.()
    handleClose()
  }

  const handleSendAgain = () => {
    setEmailSubmitted(false)
    setSubmittedEmail("")
    emailForm.reset()
  }

  if (emailSubmitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Verification Code</DialogTitle>
          </DialogHeader>
          <EmailVerificationStep
            email={submittedEmail}
            onVerify={handleVerify}
            onSendAgain={handleSendAgain}
            onCancel={handleClose}
            verificationError={userEmailVerifyResult.error}
            isVerifying={userEmailVerifyResult.loading}
            description="A verification code was sent to the email provided. Please check your email and input the code provided."
          />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Another Email Address</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            A verification code will be sent to the email provided. This helps us verify your
            account email.
          </p>
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
              <TextField
                control={emailForm.control}
                name="email"
                label="Email Address"
                placeholder="Enter your email..."
              />
              {userEmailCreateResult.error && (
                <GraphqlError error={userEmailCreateResult.error} variant="red-box" />
              )}
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={emailForm.handleSubmit(onEmailSubmit)}
            disabled={userEmailCreateResult.loading}
          >
            {userEmailCreateResult.loading ? "Sending..." : "Submit Email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
